import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  Play,
  Pause,
  Search,
  MoreVertical,
  Eye,
  Calendar,
  Zap,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Bot,
  Phone,
  Link2,
  Unlink,
  ChevronDown,
  X,
  AlertCircle,
  Clock,
} from "lucide-react";
import {
  getFlows,
  deleteFlow,
  toggleFlowStatus,
  allocateFlowToPhone,
  deallocateFlowFromPhone,
  getFlowsByPhoneId,
} from "../apis/flowAPI";
import {
  fetchAiPhoneSettings,
  toggleAiForPhone,
  updateAiKnowledgeBase,
  updateAiChatbotSettings,
} from "../apis/aiChatbotAPI";
import AiChatbotModal from "../components/flowList/AiChatbotModal";
import { toast } from "react-toastify";
import PhoneSelector from "../components/PhoneSelector";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import { getServiceUsage } from "../apis/wallet";

const FlowList = () => {
  const navigate = useNavigate();
  const { selectedPhoneAccount } = usePhoneAccount();

  // Tab state
  const [activeTab, setActiveTab] = useState("manage");

  // Existing states for Manage Flows
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // AI Chatbot Modal states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPhoneOptions, setAiPhoneOptions] = useState([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState("");
  const [aiToggleLoading, setAiToggleLoading] = useState(false);
  const [aiKnowledgeDraft, setAiKnowledgeDraft] = useState("");
  const [toneDraft, setToneDraft] = useState("");
  const [outOfContextDraft, setOutOfContextDraft] = useState("");
  const [languageDraft, setLanguageDraft] = useState("");
  const [roleDraft, setRoleDraft] = useState("");
  const [tasksAndRulesDraft, setTasksAndRulesDraft] = useState("");
  const [aiReplyDays, setAiReplyDays] = useState([]);
  const [aiReplyStartTime, setAiReplyStartTime] = useState("");
  const [aiReplyEndTime, setAiReplyEndTime] = useState("");
  const [aiTimezone, setAiTimezone] = useState("Asia/Kolkata");
  const [aiReplyToButtons, setAiReplyToButtons] = useState(false);
  const [aiReplyToAll, setAiReplyToAll] = useState(false);
  const [aiButtonTexts, setAiButtonTexts] = useState([]);
  const [knowledgeSaving, setKnowledgeSaving] = useState(false);
  const [aiListLoading, setAiListLoading] = useState(false);

  // Allocate Flows tab states (uses PhoneAccountContext for phone selection)
  const [allocatedFlows, setAllocatedFlows] = useState([]);
  const [allocatedLoading, setAllocatedLoading] = useState(false);
  const [allFlows, setAllFlows] = useState([]);
  const [showAddFlowDropdown, setShowAddFlowDropdown] = useState(false);
  const [allocatingFlowId, setAllocatingFlowId] = useState(null);
  const [deallocatingFlowId, setDeallocatingFlowId] = useState(null);

  useEffect(() => {
    fetchFlows();
  }, [filter]);

  useEffect(() => {
    loadAiPhones();
  }, []);

  // Service (session) message usage per WhatsApp number — Meta gives a
  // monthly free allowance per number, then bot/agent replies bill at the
  // utility rate (change effective 1 Oct 2026).
  const [serviceUsage, setServiceUsage] = useState(null);
  useEffect(() => {
    getServiceUsage()
      .then((res) => setServiceUsage(res.data?.data || null))
      .catch(() => setServiceUsage(null));
  }, []);

  useEffect(() => {
    if (activeTab === "allocate" && allFlows.length === 0) {
      getFlows({}).then((res) => setAllFlows(res.data.flows || [])).catch(() => {});
    }
  }, [activeTab]);

  useEffect(() => {
    const waAccountId = selectedPhoneAccount?.id;
    if (activeTab === "allocate" && waAccountId) {
      fetchAllocatedFlows(waAccountId);
    }
  }, [selectedPhoneAccount?.id, activeTab]);

  useEffect(() => {
    if (!aiModalOpen || aiPhoneOptions.length === 0) {
      return;
    }
    if (!selectedPhoneId) {
      const first = aiPhoneOptions[0];
      if (first) {
        setSelectedPhoneId(first.phone_id);
        loadPhoneData(first);
      }
    }
  }, [aiModalOpen, aiPhoneOptions, selectedPhoneId]);

  const loadPhoneData = (phone) => {
    setAiKnowledgeDraft(phone.knowledge_base || "");
    setToneDraft(phone.tone || "");
    setOutOfContextDraft(phone.out_of_context_reply || "");
    setLanguageDraft(phone.language || "");
    setRoleDraft(phone.role || "");
    setTasksAndRulesDraft(phone.tasks_and_rules || "");
    setAiReplyDays(phone.ai_reply_days || []);
    setAiReplyStartTime(phone.ai_reply_start_time || "");
    setAiReplyEndTime(phone.ai_reply_end_time || "");
    setAiTimezone(phone.ai_timezone || "Asia/Kolkata");
    setAiReplyToButtons(phone.ai_reply_to_buttons || false);
    setAiReplyToAll(phone.ai_reply_to_all || false);
    // Ensure ai_button_texts is always an array
    const buttonTexts = phone.ai_button_texts;
    if (Array.isArray(buttonTexts)) {
      setAiButtonTexts(buttonTexts);
    } else if (typeof buttonTexts === 'string' && buttonTexts.trim()) {
      // Handle comma-separated string (backward compatibility)
      setAiButtonTexts(buttonTexts.split(',').map(t => t.trim()).filter(t => t));
    } else {
      setAiButtonTexts([]);
    }
  };

  const filteredFlows = flows.filter(
    (flow) =>
      flow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (flow.description &&
        flow.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const selectedPhone = useMemo(
    () => aiPhoneOptions.find((option) => option.phone_id === selectedPhoneId),
    [aiPhoneOptions, selectedPhoneId]
  );

  const unallocatedFlows = useMemo(() => {
    const allocatedIds = allocatedFlows.map((f) => f.id);
    return allFlows.filter((flow) => !allocatedIds.includes(flow.id));
  }, [allFlows, allocatedFlows]);

  const fetchFlows = async () => {
    try {
      setLoading(true);
      const params = filter !== "all" ? { isActive: filter === "active" } : {};
      const response = await getFlows(params);
      setFlows(response.data.flows);
    } catch (error) {
      console.error("Error fetching flows:", error);
      const errorResponse = error.response?.data;
      // Don't show error for subscription issues - SubscriptionGate handles it
      if (!errorResponse?.requiresSubscription) {
        toast.error(errorResponse?.error || "Failed to load flows");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAiPhones = async () => {
    try {
      setAiListLoading(true);
      const response = await fetchAiPhoneSettings();
      const list = response.data?.data || [];
      setAiPhoneOptions(list);
      if (list.length > 0 && !selectedPhoneId) {
        setSelectedPhoneId(list[0].phone_id);
        loadPhoneData(list[0]);
      }
    } catch (error) {
      console.error("Error loading AI phone settings:", error);
    } finally {
      setAiListLoading(false);
    }
  };

  const fetchAllocatedFlows = async (waAccountId) => {
    try {
      setAllocatedLoading(true);
      const response = await getFlowsByPhoneId(waAccountId);
      setAllocatedFlows(response.data.flows || []);
    } catch (error) {
      console.error("Error fetching allocated flows:", error);
      setAllocatedFlows([]);
    } finally {
      setAllocatedLoading(false);
    }
  };

  const handleAllocateFlow = async (flowId) => {
    const waAccountId = selectedPhoneAccount?.id;
    if (!waAccountId) return;
    try {
      setAllocatingFlowId(flowId);
      await allocateFlowToPhone(flowId, waAccountId);
      await fetchAllocatedFlows(waAccountId);
      setShowAddFlowDropdown(false);
    } catch (error) {
      console.error("Error allocating flow:", error);
      alert("Failed to allocate flow");
    } finally {
      setAllocatingFlowId(null);
    }
  };

  const handleDeallocateFlow = async (flowId) => {
    const waAccountId = selectedPhoneAccount?.id;
    if (!waAccountId) return;
    if (
      !window.confirm(
        "Are you sure you want to remove this flow from this phone?"
      )
    )
      return;
    try {
      setDeallocatingFlowId(flowId);
      await deallocateFlowFromPhone(flowId, waAccountId);
      await fetchAllocatedFlows(waAccountId);
    } catch (error) {
      console.error("Error deallocating flow:", error);
      alert("Failed to remove flow");
    } finally {
      setDeallocatingFlowId(null);
    }
  };

  const handleAiToggle = async (nextValue) => {
    if (!selectedPhone) return;
    if (nextValue && !aiKnowledgeDraft.trim()) {
      const confirmed = window.confirm(
        "Warning: No knowledge base has been provided. The AI chatbot may not function properly without a knowledge base. Do you want to enable it anyway? You can add the knowledge base later."
      );
      if (!confirmed) {
        return;
      }
    }
    try {
      setAiToggleLoading(true);
      await toggleAiForPhone(selectedPhone.phone_id, nextValue);
      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? { ...option, ai_enabled: nextValue }
            : option
        )
      );
    } catch (error) {
      console.error("Error toggling AI chatbot:", error);
      alert("Failed to update AI chatbot setting");
    } finally {
      setAiToggleLoading(false);
    }
  };

  const handleKnowledgeSave = async () => {
    if (!selectedPhone) return;
    try {
      setKnowledgeSaving(true);
      await updateAiKnowledgeBase(selectedPhone.phone_id, aiKnowledgeDraft);
      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? { ...option, knowledge_base: aiKnowledgeDraft }
            : option
        )
      );
      alert("Knowledge base saved successfully");
    } catch (error) {
      console.error("Error saving knowledge base:", error);
      alert("Failed to save knowledge base");
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleOpenAiModal = async () => {
    if (aiPhoneOptions.length === 0) {
      await loadAiPhones();
    }
    setAiModalOpen(true);
  };

  const handleSelectPhone = (phoneId) => {
    setSelectedPhoneId(phoneId);
    const next = aiPhoneOptions.find((option) => option.phone_id === phoneId);
    if (next) {
      loadPhoneData(next);
    }
  };

  const handleSaveAllSettings = async (updates = {}) => {
    if (!selectedPhone) return;
    try {
      setKnowledgeSaving(true);
      const settingsToSave = {
        knowledge_base:
          updates.knowledge_base !== undefined
            ? updates.knowledge_base
            : aiKnowledgeDraft,
        tone: updates.tone !== undefined ? updates.tone : toneDraft,
        out_of_context_reply:
          updates.out_of_context_reply !== undefined
            ? updates.out_of_context_reply
            : outOfContextDraft,
        language:
          updates.language !== undefined ? updates.language : languageDraft,
        role: updates.role !== undefined ? updates.role : roleDraft,
        tasks_and_rules:
          updates.tasks_and_rules !== undefined
            ? updates.tasks_and_rules
            : tasksAndRulesDraft,
        ai_reply_days:
          updates.ai_reply_days !== undefined
            ? updates.ai_reply_days
            : aiReplyDays,
        ai_reply_start_time:
          updates.ai_reply_start_time !== undefined
            ? updates.ai_reply_start_time
            : aiReplyStartTime,
        ai_reply_end_time:
          updates.ai_reply_end_time !== undefined
            ? updates.ai_reply_end_time
            : aiReplyEndTime,
        ai_timezone:
          updates.ai_timezone !== undefined ? updates.ai_timezone : aiTimezone,
        ai_reply_to_buttons:
          updates.ai_reply_to_buttons !== undefined
            ? updates.ai_reply_to_buttons
            : aiReplyToButtons,
        ai_reply_to_all:
          updates.ai_reply_to_all !== undefined
            ? updates.ai_reply_to_all
            : aiReplyToAll,
        ai_button_texts:
          updates.ai_button_texts !== undefined
            ? updates.ai_button_texts
            : aiButtonTexts,
      };

      await updateAiChatbotSettings(selectedPhone.phone_id, settingsToSave);

      if (updates.knowledge_base !== undefined)
        setAiKnowledgeDraft(updates.knowledge_base);
      if (updates.tone !== undefined) setToneDraft(updates.tone);
      if (updates.out_of_context_reply !== undefined)
        setOutOfContextDraft(updates.out_of_context_reply);
      if (updates.language !== undefined) setLanguageDraft(updates.language);
      if (updates.role !== undefined) setRoleDraft(updates.role);
      if (updates.tasks_and_rules !== undefined)
        setTasksAndRulesDraft(updates.tasks_and_rules);
      if (updates.ai_reply_days !== undefined)
        setAiReplyDays(updates.ai_reply_days);
      if (updates.ai_reply_start_time !== undefined)
        setAiReplyStartTime(updates.ai_reply_start_time);
      if (updates.ai_reply_end_time !== undefined)
        setAiReplyEndTime(updates.ai_reply_end_time);
      if (updates.ai_timezone !== undefined) setAiTimezone(updates.ai_timezone);
      if (updates.ai_reply_to_buttons !== undefined)
        setAiReplyToButtons(updates.ai_reply_to_buttons);
      if (updates.ai_reply_to_all !== undefined)
        setAiReplyToAll(updates.ai_reply_to_all);
      if (updates.ai_button_texts !== undefined)
        setAiButtonTexts(updates.ai_button_texts);

      setAiPhoneOptions((prev) =>
        prev.map((option) =>
          option.phone_id === selectedPhone.phone_id
            ? {
                ...option,
                knowledge_base: settingsToSave.knowledge_base,
                tone: settingsToSave.tone,
                out_of_context_reply: settingsToSave.out_of_context_reply,
                language: settingsToSave.language,
                role: settingsToSave.role,
                tasks_and_rules: settingsToSave.tasks_and_rules,
                ai_reply_days: settingsToSave.ai_reply_days,
                ai_reply_start_time: settingsToSave.ai_reply_start_time,
                ai_reply_end_time: settingsToSave.ai_reply_end_time,
                ai_timezone: settingsToSave.ai_timezone,
                ai_reply_to_buttons: settingsToSave.ai_reply_to_buttons,
                ai_reply_to_all: settingsToSave.ai_reply_to_all,
                ai_button_texts: settingsToSave.ai_button_texts,
              }
            : option
        )
      );
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings");
      throw error;
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleDelete = async (flowId) => {
    if (!window.confirm("Are you sure you want to delete this flow?")) return;

    try {
      await deleteFlow(flowId);
      alert("Flow deleted successfully");
      fetchFlows();
    } catch (error) {
      console.error("Error deleting flow:", error);
      alert("Failed to delete flow");
    }
  };

  const handleToggleStatus = async (flowId, currentStatus) => {
    try {
      await toggleFlowStatus(flowId, !currentStatus);
      alert(
        `Flow ${!currentStatus ? "activated" : "deactivated"} successfully`
      );
      fetchFlows();
    } catch (error) {
      console.error("Error toggling flow status:", error);
      alert("Failed to update flow status");
    }
  };

  const getTriggerBadge = (triggerType) => {
    const badges = {
      keyword: { tone: "b", label: "Keyword" },
      welcome: { tone: "g", label: "Welcome" },
      menu: { tone: "p", label: "Menu" },
      default: { tone: "n", label: "Default" },
      api: { tone: "a", label: "API" },
    };
    const badge = badges[triggerType] || badges.default;
    return <span className={`ping-chip ${badge.tone}`}>{badge.label}</span>;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, duration: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div className="fl-page">
      <PhoneSelector section="flows" />
      <div className="page-head">
        <div>
          <h2>Flows</h2>
          <p>Create and manage automated conversation flows</p>
        </div>
        <div className="grow" />
        <button
          type="button"
          onClick={handleOpenAiModal}
          className="ping-btn ping-btn-ghost ping-btn-sm"
        >
          <Bot size={16} />
          AI chatbot
        </button>
        <button
          type="button"
          onClick={() => navigate("/flow-builder")}
          className="ping-btn ping-btn-green ping-btn-sm"
        >
          <Plus size={16} />
          Create flow
        </button>
      </div>

      <div className="fl-kpis">
        <div className="fl-kpi hi">
          <div>
            <div className="k">Total flows</div>
            <div className="v">{flows.length}</div>
          </div>
          <div className="ico"><MessageSquare size={14} /></div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Active</div>
            <div className="v">{flows.filter((f) => f.is_active).length}</div>
          </div>
          <div className="ico"><CheckCircle2 size={14} /></div>
        </div>
        <div className="fl-kpi">
          <div>
            <div className="k">Inactive</div>
            <div className="v">{flows.filter((f) => !f.is_active).length}</div>
          </div>
          <div className="ico"><XCircle size={14} /></div>
        </div>
      </div>

      {/* Service message allowance per WhatsApp number (Meta, from 1 Oct 2026):
          bot / agent / live-chat replies are free up to the monthly allowance,
          then billed at the utility rate. */}
      {serviceUsage?.numbers?.length > 0 && (
        <div
          className="fl-service-usage"
          style={{
            background: "#fff",
            border: "1px solid #e6e9ef",
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <MessageSquare size={14} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              Service messages this month ({serviceUsage.month})
            </span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              — first {Number(serviceUsage.allowance).toLocaleString("en-IN")} free per number,
              then ₹{Number(serviceUsage.overage_rate || 0).toFixed(3)} each
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {serviceUsage.numbers.map((n) => {
              const allowance = Number(serviceUsage.allowance) || 0;
              const used = Number(n.used) || 0;
              const pct = allowance > 0 ? Math.min((used / allowance) * 100, 100) : 100;
              const over = used > allowance;
              return (
                <div
                  key={n.phone_number_id}
                  style={{
                    minWidth: 220,
                    flex: "1 1 220px",
                    border: "1px solid #eef1f5",
                    borderRadius: 10,
                    padding: "8px 12px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span style={{ fontWeight: 600 }}>{n.phone_number || n.phone_number_id}</span>
                    <span style={{ color: over ? "#dc2626" : "#6b7280" }}>
                      {used.toLocaleString("en-IN")} / {allowance.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div
                    style={{
                      height: 6,
                      borderRadius: 3,
                      background: "#eef1f5",
                      marginTop: 6,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        borderRadius: 3,
                        background: over ? "#dc2626" : pct >= 80 ? "#f59e0b" : "#16a34a",
                        transition: "width .3s",
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                    {over
                      ? `${n.charged_count.toLocaleString("en-IN")} charged · ₹${Number(n.charged_amount).toFixed(2)} spent`
                      : `${n.free_remaining.toLocaleString("en-IN")} free messages left`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="ping-pill-tabs">
        <button
          type="button"
          onClick={() => setActiveTab("manage")}
          className={`ping-ptab ${activeTab === "manage" ? "on" : ""}`}
        >
          <MessageSquare size={14} />
          Manage
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("allocate")}
          className={`ping-ptab ${activeTab === "allocate" ? "on" : ""}`}
        >
          <Link2 size={14} />
          Allocate to numbers
        </button>
      </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "manage" ? (
            <motion.div
              key="manage"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="fl-toolbar">
                <div className="fl-search">
                  <Search size={15} />
                  <input
                    type="text"
                    placeholder="Search flows…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="fl-filters">
                  {["all", "active", "inactive"].map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setFilter(tab)}
                      className={`ping-ptab ${filter === tab ? "on" : ""}`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Flows Grid */}
              <AnimatePresence>
                {loading ? (
                  <motion.div
                    className="fl-grid"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="fl-card fl-skel">
                        <div className="fl-skel-line" />
                        <div className="fl-skel-line short" />
                      </div>
                    ))}
                  </motion.div>
                ) : filteredFlows.length === 0 ? (
                  <motion.div
                    className="fl-empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="fl-empty-ico">
                      <MessageSquare size={22} />
                    </div>
                    <h3>{searchQuery ? "No flows found" : "No flows yet"}</h3>
                    <p>
                      {searchQuery
                        ? `No flows match “${searchQuery}”.`
                        : "Create a flow to automate conversations."}
                    </p>
                    {!searchQuery && (
                      <button
                        type="button"
                        onClick={() => navigate("/flow-builder")}
                        className="ping-btn ping-btn-green ping-btn-sm"
                      >
                        <Plus size={16} />
                        Create flow
                      </button>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    className="fl-grid"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {filteredFlows.map((flow, index) => (
                      <motion.div
                        key={flow.id}
                        variants={itemVariants}
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.06, duration: 0.45 }}
                      >
                        <FlowCard
                          flow={flow}
                          onEdit={() => navigate(`/flows/builder/${flow.id}`)}
                          onView={() => navigate(`/flows/builder/${flow.id}`)}
                          onDelete={() => handleDelete(flow.id)}
                          onToggleStatus={() =>
                            handleToggleStatus(flow.id, flow.is_active)
                          }
                          getTriggerBadge={getTriggerBadge}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              key="allocate"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <AllocateFlowsTab
                selectedPhoneAccount={selectedPhoneAccount}
                allocatedFlows={allocatedFlows}
                loading={allocatedLoading}
                unallocatedFlows={unallocatedFlows}
                showAddDropdown={showAddFlowDropdown}
                setShowAddDropdown={setShowAddFlowDropdown}
                onAllocate={handleAllocateFlow}
                onDeallocate={handleDeallocateFlow}
                allocatingFlowId={allocatingFlowId}
                deallocatingFlowId={deallocatingFlowId}
                getTriggerBadge={getTriggerBadge}
              />
            </motion.div>
          )}
        </AnimatePresence>

      {aiModalOpen && (
        <AiChatbotModal
          onClose={() => setAiModalOpen(false)}
          loading={aiListLoading}
          phoneOptions={aiPhoneOptions}
          selectedPhoneId={selectedPhoneId}
          onSelectPhone={handleSelectPhone}
          aiEnabled={!!selectedPhone?.ai_enabled}
          onToggleAi={handleAiToggle}
          toggleLoading={aiToggleLoading}
          knowledgeDraft={aiKnowledgeDraft}
          onKnowledgeChange={setAiKnowledgeDraft}
          toneDraft={toneDraft}
          onToneChange={setToneDraft}
          outOfContextDraft={outOfContextDraft}
          onOutOfContextChange={setOutOfContextDraft}
          languageDraft={languageDraft}
          onLanguageChange={setLanguageDraft}
          roleDraft={roleDraft}
          onRoleChange={setRoleDraft}
          tasksAndRulesDraft={tasksAndRulesDraft}
          onTasksAndRulesChange={setTasksAndRulesDraft}
          aiReplyDays={aiReplyDays}
          aiReplyStartTime={aiReplyStartTime}
          aiReplyEndTime={aiReplyEndTime}
          aiTimezone={aiTimezone}
          onScheduleChange={(data) => {
            setAiReplyDays(data.ai_reply_days);
            setAiReplyStartTime(data.ai_reply_start_time);
            setAiReplyEndTime(data.ai_reply_end_time);
            setAiTimezone(data.ai_timezone);
          }}
          aiReplyToButtons={aiReplyToButtons}
          onAiReplyToButtonsChange={setAiReplyToButtons}
          aiReplyToAll={aiReplyToAll}
          onAiReplyToAllChange={setAiReplyToAll}
          aiButtonTexts={aiButtonTexts}
          onAiButtonTextsChange={setAiButtonTexts}
          onSaveAll={handleSaveAllSettings}
          saving={knowledgeSaving}
        />
      )}

    </div>
  );
};

// Allocate Flows Tab Component - uses PhoneSelector at page level
const AllocateFlowsTab = ({
  selectedPhoneAccount,
  allocatedFlows,
  loading,
  unallocatedFlows,
  showAddDropdown,
  setShowAddDropdown,
  onAllocate,
  onDeallocate,
  allocatingFlowId,
  deallocatingFlowId,
  getTriggerBadge,
}) => {
  const [searchFlow, setSearchFlow] = useState("");

  const filteredUnallocatedFlows = unallocatedFlows.filter(
    (flow) =>
      flow.name.toLowerCase().includes(searchFlow.toLowerCase()) ||
      (flow.description &&
        flow.description.toLowerCase().includes(searchFlow.toLowerCase()))
  );

  return (
    <div className="fl-alloc">
      <div className="fl-alloc-card">
        <div className="fl-alloc-head">
          <div>
            <h3>Allocated flows</h3>
            <p>
              {selectedPhoneAccount
                ? `Assigned to ${
                    selectedPhoneAccount.phoneName || selectedPhoneAccount.phoneNumber
                  }`
                : "Select a phone above to view allocated flows"}
            </p>
          </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAddDropdown(!showAddDropdown)}
                className="ping-btn ping-btn-green ping-btn-sm"
                disabled={!selectedPhoneAccount?.id}
              >
                <Plus size={16} />
                Add flow
                <ChevronDown
                  size={14}
                  className={`transition-transform ${
                    showAddDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Add Flow Dropdown */}
              <AnimatePresence>
                {showAddDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowAddDropdown(false)}
                    />
                    <motion.div
                      className="fl-add-menu"
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                    >
                      <div className="fl-search">
                        <Search size={15} />
                        <input
                          type="text"
                          placeholder="Search flows…"
                          value={searchFlow}
                          onChange={(e) => setSearchFlow(e.target.value)}
                          autoFocus
                        />
                      </div>

                      {/* Flow List */}
                      <div className="max-h-64 overflow-y-auto">
                        {filteredUnallocatedFlows.length === 0 ? (
                          <div className="p-6 text-center">
                            <p className="text-sm text-gray-500 font-medium">
                              {unallocatedFlows.length === 0
                                ? "All flows are already allocated"
                                : "No flows match your search"}
                            </p>
                          </div>
                        ) : (
                          filteredUnallocatedFlows.map((flow) => (
                            <button
                              key={flow.id}
                              onClick={() => onAllocate(flow.id)}
                              disabled={allocatingFlowId === flow.id}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between gap-3 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-gray-900 truncate">
                                  {flow.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate font-medium">
                                  {flow.description || "No description"}
                                </p>
                              </div>
                              {allocatingFlowId === flow.id ? (
                                <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Link2
                                  size={16}
                                  className="text-gray-400 flex-shrink-0"
                                />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
        </div>

        <div className="fl-alloc-body">
          {loading ? (
            <div className="fl-empty">Loading allocated flows…</div>
          ) : allocatedFlows.length === 0 ? (
            <div className="fl-empty">
              <div className="fl-empty-ico">
                <Unlink size={22} />
              </div>
              <h3>No flows allocated</h3>
              <p>Add a flow to assign it to this number.</p>
            </div>
          ) : (
            <div className="fl-alloc-list">
              {allocatedFlows.map((flow) => (
                <div key={flow.id} className="fl-alloc-row">
                  <div className="min-w-0">
                    <div className="fl-card-top">
                      <h3>{flow.name}</h3>
                      <span className={`ping-chip ${flow.is_active ? "g" : "n"}`}>
                        {flow.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p className="fl-card-desc">{flow.description || "No description"}</p>
                    <div className="fl-card-chips">{getTriggerBadge(flow.trigger_type)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeallocate(flow.id)}
                    disabled={deallocatingFlowId === flow.id}
                    className="camp-ico danger"
                    data-tip="Remove"
                    aria-label="Remove flow"
                  >
                    {deallocatingFlowId === flow.id ? (
                      <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <X size={14} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Flow Card Component
const FlowCard = ({
  flow,
  onEdit,
  onView,
  onDelete,
  onToggleStatus,
  getTriggerBadge,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const statusTone = flow.is_expired ? "r" : flow.is_active ? "g" : "n";
  const statusLabel = flow.is_expired ? "Expired" : flow.is_active ? "Active" : "Inactive";

  return (
    <article className="fl-card" onClick={onView}>
      <div className="fl-card-top">
        <h3>{flow.name}</h3>
        <span className={`ping-chip ${statusTone}`}>{statusLabel}</span>
        <div className="relative">
          <button
            type="button"
            className="camp-ico"
            data-tip="More"
            aria-label="More actions"
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
          >
            <MoreVertical size={14} />
          </button>
          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                }}
              />
              <div className="fl-menu">
                <button type="button" onClick={(e) => { e.stopPropagation(); onView(); setShowMenu(false); }}>
                  <Eye size={14} /> View
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(); setShowMenu(false); }}>
                  <Edit size={14} /> Edit
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); onToggleStatus(); setShowMenu(false); }}>
                  {flow.is_active ? <Pause size={14} /> : <Play size={14} />}
                  {flow.is_active ? "Deactivate" : "Activate"}
                </button>
                <button type="button" className="danger" onClick={(e) => { e.stopPropagation(); onDelete(); setShowMenu(false); }}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <p className="fl-card-desc">{flow.description || "No description"}</p>

      <div className="fl-card-chips">
        {getTriggerBadge(flow.trigger_type)}
        {flow.expires_at && !flow.is_expired && flow.days_until_expiry != null && flow.days_until_expiry <= 7 && (
          <span className="ping-chip a">
            {flow.days_until_expiry <= 0
              ? "Expires today"
              : flow.days_until_expiry === 1
                ? "Expires tomorrow"
                : `${flow.days_until_expiry} days left`}
          </span>
        )}
        {flow.trigger_type === "keyword" && flow.trigger_keywords?.length > 0 && (
          flow.trigger_keywords.length === 1 && flow.trigger_keywords[0] === "__any__" ? (
            <span className="ping-chip g">Any message</span>
          ) : (
            <>
              {flow.trigger_keywords.slice(0, 3).map((keyword, idx) => (
                <span key={idx} className="ping-chip b">{keyword}</span>
              ))}
              {flow.trigger_keywords.length > 3 && (
                <span className="ping-chip n">+{flow.trigger_keywords.length - 3}</span>
              )}
            </>
          )
        )}
      </div>

      <div className="fl-card-foot">
        <span className="fl-date">
          <Calendar size={12} />
          {new Date(flow.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>
        {flow.expires_at && (
          <span className={`fl-date ${flow.is_expired ? "bad" : flow.days_until_expiry <= 7 ? "warn" : ""}`}>
            {flow.is_expired ? <AlertCircle size={12} /> : <Clock size={12} />}
            {flow.is_expired
              ? "Expired"
              : flow.days_until_expiry <= 0
                ? "Expires today"
                : flow.days_until_expiry === 1
                  ? "Expires in 1 day"
                  : `Expires in ${flow.days_until_expiry} days`}
          </span>
        )}
        <div className="fl-actions">
          <button type="button" className="camp-ico" data-tip="View" aria-label="View" onClick={(e) => { e.stopPropagation(); onView(); }}>
            <Eye size={14} />
          </button>
          <button type="button" className="camp-ico" data-tip="Edit" aria-label="Edit" onClick={(e) => { e.stopPropagation(); onEdit(); }}>
            <Edit size={14} />
          </button>
        </div>
      </div>
    </article>
  );
};

export default FlowList;
